import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthAxios from '../../../utils/useAuthAxios';
import BespokeService, {
	CreationBespokeService,
} from '../../../Services/BespokeService';

import useAlert from '../../../Hooks/useAlert';
import SpinnerOverlay from '../../Ui/SpinnerOverlay';
import { useSelector, useDispatch } from 'react-redux';
import { BespokeThunk } from '../../../store/services/bespokeThunk';
import { bespokeActions } from '../../../store/bespokeProperty-slice';
import { bespokeFormActions } from '../../../store/bespokeForm-slice';
import BespokeCopyVersionSelect from './BespokeCopyVersionSelect';
import {
	getCurrentTemplateVersion,
	normalizeTemplateVersionItem,
	sortTemplateVersions,
} from '../Edition/bespokeTemplateVersionUtils';

export default function CreationNewBespoke() {

	const {
		env,
		newDataBespokeCopy,
		idAccountOfBespokeToCopy,
	} = useSelector((state) => state.bespoke);
	const {
		newBespokeValues,
	} = useSelector((state) => state.bespokeForm);
	const dispatch = useDispatch();
	const [allBespokes, setAllBespokes] = useState();
	const [bespokeByEnvSelected, setbespokeByEnvSelected] = useState();
	const [bespokesOfAccount, setBespokesOfAccount] = useState();
	const [copyMode, setCopyMode] = useState('');
	const [copyEnv, setCopyEnv] = useState('');
	const [bespokeToCopy, setBespokeToCopy] = useState('');
	const [sourceTemplateVersions, setSourceTemplateVersions] = useState([]);
	const [selectedSourceTemplateVersion, setSelectedSourceTemplateVersion] = useState('');
	const [versionsLoading, setVersionsLoading] = useState(false);
	const [copyLoading, setCopyLoading] = useState(false);

	const accountId = newBespokeValues && newBespokeValues.accountId;
	const [redirect, setRedirect] = useState(false);

	const { isAuthenticated } = useAuth0();
	const { authAxios } = useAuthAxios();
	const { createAlerte } = useAlert();
	const creationBespokeService = new CreationBespokeService(authAxios);
	const bespokeService = new BespokeService(authAxios);
	function insensible_a_la_casse(a, b) {
		if (a.account.name === null || b.account.name === null) return 0;
		if (a.account.name.toUpperCase() < b.account.name.toUpperCase())
			return -1;
		if (a.account.name.toUpperCase() > b.account.name.toUpperCase())
			return 1;
		return 0;
	}

	const fetchDataPropertiesAccount = () => {
		dispatch(
			BespokeThunk.getAccountPropertiesCopy({ authAxios, env, accountId })
		);
	};
	const fetchDataPropertyBespokeCopy = (templateVersion) => {
		return dispatch(
			BespokeThunk.getBespokePropertyById({
				authAxios,
				copyEnv,
				idAccountOfBespokeToCopy,
				bespokeToCopy,
				templateVersion:
					templateVersion ?? newDataBespokeCopy.SourceBespokeTemplateVersion,
			})
		);
	};

	const buildBespokeCopyPayload = async () => {
		let payload = { ...newDataBespokeCopy };

		if (
			!payload.SourceBespokeTemplateVersion &&
			payload.SourceBespokeId &&
			payload.SourceEnvironment
		) {
			const result = await bespokeService.getBespokeProperty(
				payload.SourceEnvironment,
				payload.SourceBespokeId,
				(err) => {
					createAlerte('error', 'An error occurred', {
						removeAllBeforeShow: false,
					});
					console.log(err);
				}
			);
			if (result?.data?.templateVersion != null) {
				payload.SourceBespokeTemplateVersion = result.data.templateVersion;
			}
		}

		return payload;
	};

	const loadSourceTemplateVersions = async (sourceEnv, sourceBespokeId) => {
		setVersionsLoading(true);
		setSourceTemplateVersions([]);
		setSelectedSourceTemplateVersion('');

		const result = await bespokeService.getBespokeTemplateVersions(
			sourceEnv,
			sourceBespokeId,
			(err) => {
				createAlerte('error', 'An error occurred while loading template versions', {
					removeAllBeforeShow: false,
				});
				console.log(err);
			}
		);

		if (result?.data) {
			const versions = sortTemplateVersions(
				result.data.map(normalizeTemplateVersionItem)
			);
			setSourceTemplateVersions(versions);
			const defaultVersion =
				getCurrentTemplateVersion(versions) ?? versions[0]?.templateVersion ?? '';
			setSelectedSourceTemplateVersion(defaultVersion);
			dispatch(bespokeActions.handleNewDataBespokeCopy({
				SourceBespokeTemplateVersion: defaultVersion,
			}));
		}

		setVersionsLoading(false);
	};

	const runCopy = async (e) => {
		e.preventDefault();
		if (!bespokeToCopy || !selectedSourceTemplateVersion) {
			return;
		}

		setCopyLoading(true);
		try {
			const copyPayload = await buildBespokeCopyPayload();
			const res = await creationBespokeService.updateBespokeByEnv(env,
				copyPayload,
				(err) => {
					createAlerte('error', 'An error occurred', {
						removeAllBeforeShow: false,
					});
				});
			dispatch(bespokeActions.updateBespokeTemplate(res));
			dispatch(bespokeFormActions.handleNewBespokeValues({
				name: res.name,
				description: res.description,
				code: res.code + 'Copy',
				accountId: accountId,
				format: res.format,
				extensionFormat: res.extensionFormat,
				customCssEnabled: res.customCssEnabled,
				mobileVersionEnabled: res.mobileVersionEnabled,
				thumbnailUrl: res.thumbnailUrl,
				thumbnailDisplayUrl: res.thumbnailDisplayUrl,
			}));

			fetchDataPropertiesAccount();
			await fetchDataPropertyBespokeCopy(copyPayload.SourceBespokeTemplateVersion);
			setRedirect(true);
		} finally {
			setCopyLoading(false);
		}
	};

	useEffect(() => {
		const fetchBespokes = async () => {
			const result = await bespokeService.getBespokesList((err) => {
				createAlerte('error', 'An error occurred', {
					removeAllBeforeShow: false,
				});
				console.log(err);
			});
			setAllBespokes(result.data);
		};
		if (isAuthenticated) fetchBespokes();
	}, [isAuthenticated]);

	useEffect(() => {
		if (copyEnv !== '') {
			let filterByEnv = allBespokes.filter((be) => be.env === copyEnv);
			filterByEnv.sort(insensible_a_la_casse);
			let filtereByAccountByEnv =
                filterByEnv &&
                filterByEnv.filter((ac) => ac.account.id === accountId);
			dispatch(bespokeActions.handleidAccountOfBespokeToCopy(accountId));
			setBespokesOfAccount(filtereByAccountByEnv);
			setbespokeByEnvSelected(filterByEnv);
		}
	}, [copyEnv]);

	const handleFilterCopyMode = (e) => {
		setCopyMode(e.target.value);
		setBespokeToCopy('');
		setSourceTemplateVersions([]);
		setSelectedSourceTemplateVersion('');
	};

	const handleFilterCopyEnv = (e) => {
		const envToCopyValue = e.target.value;
		setCopyEnv(envToCopyValue);
		setBespokeToCopy('');
		setSourceTemplateVersions([]);
		setSelectedSourceTemplateVersion('');
		dispatch(
			bespokeActions.handleNewDataBespokeCopy({
				SourceEnvironment: envToCopyValue,
			})
		);
	};

	const handleCopyBespoke = async (e) => {
		const arrayOfValues = e.target.value.split(',');
		if (!arrayOfValues[0]) {
			setBespokeToCopy('');
			setSourceTemplateVersions([]);
			setSelectedSourceTemplateVersion('');
			return;
		}

		const sourceBespokeId = arrayOfValues[0];
		setBespokeToCopy(sourceBespokeId);
		dispatch(
			bespokeActions.handleidAccountOfBespokeToCopy(arrayOfValues[1])
		);
		dispatch(
			bespokeActions.handleNewDataBespokeCopy({
				TargetAccountId: arrayOfValues[1],
				SourceBespokeId: sourceBespokeId,
				SourceEnvironment: copyEnv,
				SourceBespokeTemplateVersion: '',
			})
		);
		await loadSourceTemplateVersions(copyEnv, sourceBespokeId);
	};

	const handleSourceVersionChange = (e) => {
		const version = e.target.value;
		setSelectedSourceTemplateVersion(version);
		dispatch(bespokeActions.handleNewDataBespokeCopy({
			SourceBespokeTemplateVersion: version,
		}));
	};

	const renderCopyAction = () => {
		if (!bespokeToCopy) {
			return null;
		}

		if (copyLoading) {
			return <SpinnerOverlay />;
		}

		return (
			<div
				className="btn-normal mt-4 mx-auto"
				onClick={selectedSourceTemplateVersion ? runCopy : undefined}
				style={!selectedSourceTemplateVersion ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
			>
                Copy
			</div>
		);
	};

	if (redirect) {
		return <Redirect to={'/bespokeEdition/'} />;
	}
	return (
		<div className="col-sm-10 m-auto">
			<div className="form-group m-auto col-md-6 ">
				<label htmlFor="account">Copy from </label>
				<select
					className="form-control mb-2 border  rounded-right "
					id="copyMode"
					name="copyMode"
					onChange={handleFilterCopyMode}
					value={copyMode}
				>
					<option value=""> </option>
					<option value="thisAccount"> This account </option>
					<option value="anotherAccount">Another account </option>
				</select>
			</div>
			{copyMode === 'thisAccount' && (
				<>
					<div className="form-group m-auto col-md-6 ">
						<label htmlFor="account">Environnement </label>
						<select
							className="form-control mb-2 border  rounded-right "
							id="envCopy"
							name="envCopy"
							value={copyEnv}
							onChange={handleFilterCopyEnv}
						>
							<option value=""> </option>
							<option value="PreProd"> Preprod</option>
							<option value="Prod"> Prod </option>
							<option value="Dev">Dev </option>
						</select>
					</div>

					<div className="form-group m-auto col-md-6 ">
						<label htmlFor="account">Account SaaS Formats  </label>
						{bespokesOfAccount ? (
							<select
								className="form-control mb-2 border  rounded-right "
								id="account"
								name="account"
								onChange={handleCopyBespoke}
							>
								<option value=""> </option>
								{bespokesOfAccount &&
                                    bespokesOfAccount.map((x, i) => (
                                    	<option
                                    		key={i}
                                    		value={[
                                    			x.bespoke.bespokeId,
                                    			x.bespoke.accountId,
                                    		]}
                                    	>
                                    		{' '}
                                    		{x.bespoke.code === null
                                    			? x.bespokeId.id
                                    			: x.bespoke.code}
                                    	</option>
                                    ))}
							</select>
						) : (
							<SpinnerOverlay />
						)}

						<BespokeCopyVersionSelect
							versions={sourceTemplateVersions}
							value={selectedSourceTemplateVersion}
							onChange={handleSourceVersionChange}
							loading={versionsLoading}
						/>
						{renderCopyAction()}
					</div>
				</>
			)}

			{copyMode === 'anotherAccount' && (
				<>
					<div className="form-group m-auto col-md-6 ">
						<label htmlFor="account">Environment </label>
						<select
							className="form-control mb-2 border  rounded-right "
							id="envCopy"
							name="envCopy"
							value={copyEnv}
							onChange={handleFilterCopyEnv}
						>
							<option value=""> </option>
							<option value="PreProd"> Preprod</option>
							<option value="Prod"> Prod </option>
							<option value="Dev">Dev </option>
						</select>
					</div>
					<div className="form-group m-auto col-md-6 ">
						<label htmlFor="account">
                            SaaS Formats BEYABLE accounts{' '}
						</label>
						{allBespokes ? (
							<select
								className="form-control mb-2 border  rounded-right "
								id="account"
								name="account"
								onChange={handleCopyBespoke}
							>
								<option value=""> </option>
								{bespokeByEnvSelected &&
                                    allBespokes &&
                                    bespokeByEnvSelected.map((x, i) => (
                                    	<option
                                    		key={i}
                                    		value={[
                                    			x.bespoke.bespokeId,
                                    			x.bespoke.accountId,
                                    		]}
                                    	>
                                    		{x.account.name} -{' '}
                                    		{x.bespoke.code === null
                                    			? x.bespokeId.id
                                    			: x.bespoke.code}
                                    	</option>
                                    ))}
							</select>
						) : (
							<SpinnerOverlay />
						)}

						<BespokeCopyVersionSelect
							versions={sourceTemplateVersions}
							value={selectedSourceTemplateVersion}
							onChange={handleSourceVersionChange}
							loading={versionsLoading}
						/>
						{renderCopyAction()}
					</div>
				</>
			)}
		</div>
	);
}
